import { Stack, TextField, Chip, Typography, useTheme, alpha } from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';

interface DateRange {
    from: string;
    to: string;
}

interface MetricsDateRangePickerProps {
    value: DateRange;
    onChange: (range: DateRange) => void;
}

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (base: string, days: number) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

export const MetricsDateRangePicker = ({ value, onChange }: MetricsDateRangePickerProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const t = today();

    const presets: { label: string; from: string; to: string }[] = [
        { label: LL.metricsPage.dateRange.last30(), from: addDays(t, -30), to: t },
        { label: LL.metricsPage.dateRange.last90(), from: addDays(t, -90), to: addDays(t, 30) },
        { label: LL.metricsPage.dateRange.last180(), from: addDays(t, -180), to: t },
        {
            label: LL.metricsPage.dateRange.thisYear(),
            from: `${new Date().getFullYear()}-01-01`,
            to: `${new Date().getFullYear()}-12-31`,
        },
    ];

    const isActive = (p: { from: string; to: string }) =>
        p.from === value.from && p.to === value.to;

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3,
                p: 2,
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                flexWrap: 'wrap',
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <DateRangeIcon fontSize="small" color="primary" />
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {LL.metricsPage.dateRange.label()}
                </Typography>
            </Stack>
            {/* Preset chips */}
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {presets.map((p) => (
                    <Chip
                        key={p.label}
                        label={p.label}
                        size="small"
                        color={isActive(p) ? 'primary' : 'default'}
                        variant={isActive(p) ? 'filled' : 'outlined'}
                        onClick={() => onChange({ from: p.from, to: p.to })}
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
            </Stack>
            {/* Manual inputs */}
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, ml: { sm: 'auto' } }}>
                <TextField
                    type="date"
                    size="small"
                    label={LL.metricsPage.dateRange.from()}
                    value={value.from}
                    onChange={(e) => onChange({ ...value, from: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ width: 160 }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    –
                </Typography>
                <TextField
                    type="date"
                    size="small"
                    label={LL.metricsPage.dateRange.to()}
                    value={value.to}
                    onChange={(e) => onChange({ ...value, to: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ width: 160 }}
                />
            </Stack>
        </Stack>
    );
};
